import { Button } from "@/modules/shadcn/ui/button";
import { Spinner } from "@/modules/shadcn/ui/spinner";
import { CloudOff, RefreshCw } from "lucide-react";
import { useTranslation } from "react-i18next";

export interface RelayUnavailableProps {
  /** Why the handshake failed, straight from the relay slice. */
  message: string;
  onRetry: () => void;
  isRetrying?: boolean;
  /**
   * Tightens spacing and type so the screen fits inside the 16:9 preview box
   * (Run → Inference) instead of owning the whole page (Capture).
   */
  compact?: boolean;
}

/**
 * The project reaches its camera through the relay and the relay session could
 * not be established.
 *
 * Its own screen because nothing in project settings can fix it — the previous
 * behaviour was an endless "Searching for camera…", which sent people to change
 * a configuration that was already correct.
 */
export const RelayUnavailable = ({
  message,
  onRetry,
  isRetrying,
  compact,
}: RelayUnavailableProps) => {
  const { t } = useTranslation(["camera", "common"]);
  const cx = {
    root: compact
      ? "flex h-full flex-1 flex-col items-center justify-center overflow-y-auto p-6 text-center"
      : "flex flex-1 flex-col items-center justify-center p-8",
    icon: compact
      ? "mb-4 h-14 w-14 [&_svg]:size-6"
      : "mb-6 h-20 w-20 [&_svg]:size-8",
    title: compact ? "mb-1 text-base" : "mb-2 text-xl",
    description: compact ? "mb-4 text-xs" : "mb-6 text-sm",
    button: compact ? "mb-4" : "mb-8",
    link: compact ? "text-xs" : "text-sm",
  };

  return (
    <div className={cx.root}>
      <div
        className={`flex items-center justify-center rounded-full bg-black/5 [&_svg]:text-black/60 ${cx.icon}`}
      >
        <CloudOff />
      </div>

      <p className={`font-semibold text-black ${cx.title}`}>
        {t("connection.relayUnavailable.title")}
      </p>

      <p className={`text-black/60 ${cx.description}`}>
        {t("connection.relayUnavailable.description", { message })}
      </p>

      <Button
        onClick={onRetry}
        disabled={isRetrying}
        size={compact ? "sm" : "default"}
        className={cx.button}
      >
        {isRetrying ? (
          <>
            <Spinner className="size-4" />
            {t("connection.relayUnavailable.reconnecting")}
          </>
        ) : (
          <>
            <RefreshCw />
            {t("common:actions.tryAgain")}
          </>
        )}
      </Button>

      <a
        href="https://robopipe.gitbook.io/doc/getting-started/connection"
        target="_blank"
        rel="noopener noreferrer"
        className={`text-center text-primary hover:underline ${cx.link}`}
      >
        {t("connection.learnMore")}
      </a>
    </div>
  );
};
